import { DSSeparator } from '@elliemae/ds-separator';
import { isShallowEqual } from '@frontend/pricing-search';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { DataProvider } from '../../common/utils/customHooks/useAppData';
import Session, {
  APPLICATION_NAME,
  IS_BUYSIDE,
  IS_LO,
  IS_SELLSIDE,
} from '../../common/services/Session';
import {
  floatPricingAction,
  getClientSettingSelector,
  getRateSelectorAction,
} from '../../data/lockSummary/actionCreators';
import { fetchCurrentPersonaAccessRules } from '../../data/personaAccess/actionCreators';
import { closeLoader, openLoader } from '../../data/screenLoader/actions';
import {
  KPI,
  discardInitialLoad,
  kpiEnd,
} from '../../common/services/kpiTracking';
import { AppDispatch } from '../../store';
import {
  StyledEppsContainerBody,
  StyledEppsParentView,
  StyledEppsWrapper,
} from '../global/styles/styled';
import { CommentsSection } from './components/CommentsSection';
import { DiffElements } from './components/DiffElements';
import { FooterActionButtons } from './components/FooterActionButtons';
import { shouldAutoFloat } from './components/FooterActionButtons/utils';
import { GeneralLoanInformation } from './components/GeneralLoanInformation/GeneralLoanInformation';
import { InformationGroupSection } from './components/InformationGroupSection';
import { getTableDataWithIDs } from './components/PricingDetailsModal/helpers';
import { PricingTable } from './components/PricingTable';
import { SummaryHeader } from './components/SummaryHeader';
import { StatusFailMessage } from './components/SummaryHeader/StatusFailMessage';

export const LockSummary = () => {
  const [dataTableContext, setTableDataContext] = useState<any>({
    rawPrice: [],
    adjustedPrice: [],
  });
  const dispatch = useDispatch<AppDispatch>();
  const store = useStore<any>();
  const isMountedRef = useRef(true);
  const priceData = useSelector(
    ({
      epps: {
        lockSummary: { rateSelector },
      },
    }) => rateSelector.priceData,
    isShallowEqual,
  );

  useEffect(() => {
    // TODO This code snippet was changed and needs to be refactored.
    if (priceData) setTableDataContext(getTableDataWithIDs(priceData));
  }, [priceData]);

  // Floats the selected rate once it loads, if Enable Auto Float is on and the loan can be floated.
  const autoFloat = async (rateSelectorResults) => {
    const { epps, personaAccess, EPPS } = store.getState();
    if (
      !isMountedRef.current ||
      !Session.get(IS_LO) ||
      Session.get(IS_BUYSIDE) ||
      Session.get(IS_SELLSIDE) ||
      !rateSelectorResults?.optionId ||
      !shouldAutoFloat(
        epps?.lockSummary?.clientSettingsDataSelector,
        Session.get(APPLICATION_NAME),
        personaAccess?.allowedActions,
        EPPS?.originalLoanInformation,
      )
    ) {
      return;
    }
    const { noClosingCost, lenderFeeWaiver } = rateSelectorResults.loan || {};
    await dispatch(
      floatPricingAction(
        {
          noClosingCostOption: noClosingCost,
          lenderFeeWaiverOption: lenderFeeWaiver,
        },
        { stayOnSummary: true },
      ),
    );
  };

  const initPage = async () => {
    dispatch(openLoader('Loading'));
    try {
      const [rateSelectorResults] = await Promise.all([
        dispatch(getRateSelectorAction()),
        dispatch(getClientSettingSelector()),
        // Persona rules gate the footer buttons rendered below, so they have to
        // resolve before the loader closes.
        dispatch(fetchCurrentPersonaAccessRules()),
      ]);
      if (rateSelectorResults && rateSelectorResults.code === undefined) {
        await autoFloat(rateSelectorResults);
      }
    } finally {
      dispatch(closeLoader());
      kpiEnd(KPI.SelectRate);
      discardInitialLoad();
    }
  };

  useEffect(() => {
    initPage();
    window.scrollTo(0, 0);

    return () => {
      isMountedRef.current = false;
      console.log('LockSummary unmounted, perform cleanup if necessary');
    };
  }, []);

  return (
    <StyledEppsParentView>
      <StatusFailMessage />
      <StyledEppsContainerBody>
        <StyledEppsWrapper>
          <DataProvider
            value={{
              dataTableContext,
              setTableDataContext,
            }}
          >
            <SummaryHeader />
            <GeneralLoanInformation />
            <DSSeparator
              margin="s"
              type="non-form"
              orientation="horizontal"
              position="initial"
            />
            <PricingTable />
            <InformationGroupSection />
            <CommentsSection />
          </DataProvider>
        </StyledEppsWrapper>
      </StyledEppsContainerBody>
      <FooterActionButtons />
      <DiffElements />
    </StyledEppsParentView>
  );
};
